/* Artisan's Avenue Headquarters | Ask Leo
   Internal, read-only, threaded assistant. Lives beside Admin View.
   Short chat history stays in this browser tab for the authenticated employee.
 */
(() => {
  'use strict';

  const LEO_IMAGE =
    'https://cdn.shopify.com/s/files/1/0738/6847/6555/files/leo-headquarters-natural-cutout.png?v=1791517831';
  const TRIO_IMAGE =
    'https://cdn.shopify.com/s/files/1/0738/6847/6555/files/aa-linda-leo-cora-approved-trio.png?v=1791525572';
  const MAX_TURNS = 16;
  const HISTORY_WINDOW = 6;
  const SESSION_LIFETIME = 4 * 60 * 60 * 1000;

  function make(tag, className = '', text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  }

  function safeTurn(item) {
    if (!item || !['user', 'assistant'].includes(item.role)) return null;
    const role = item.role;
    if (role === 'user') {
      if (typeof item.message !== 'string') return null;
      return { role, message: item.message.slice(0, 500) };
    }
    const kind = ['conversation', 'guide', 'insight', 'unknown'].includes(item.kind) ? item.kind : 'unknown';
    return {
      role, kind,
      title: typeof item.title === 'string' ? item.title.slice(0, 160) : '',
      message: typeof item.message === 'string' ? item.message.slice(0, 2500) : '',
      steps: Array.isArray(item.steps) ? item.steps.filter(x => typeof x === 'string').slice(0, 20).map(x => x.slice(0, 350)) : [],
      notes: Array.isArray(item.notes) ? item.notes.filter(x => typeof x === 'string' && !/^I only (read records for clients available to your account|use clients permitted for your Headquarters account)\.$/i.test(x.trim())).slice(0, 6).map(x => x.slice(0, 350)) : [],
      sources: Array.isArray(item.sources) ? item.sources.slice(0, 5).flatMap(source => {
        if (!source || typeof source.url !== 'string') return [];
        try {
          const uri = new URL(source.url);
          if (uri.protocol !== 'https:' || uri.username || uri.password) return [];
          return [{ url: uri.href, title: String(source.title || uri.hostname).slice(0, 120) }];
        } catch (_) { return []; }
      }) : [],
      url: typeof item.url === 'string' && /^\/pages\/[a-z0-9-]+$/.test(item.url) ? item.url : null,
      navigationOptions: Array.isArray(item.navigationOptions)
        ? item.navigationOptions.slice(0, 4).flatMap(option =>
            option && typeof option.label === 'string' &&
            option.label.length > 0 && option.label.length <= 60 &&
            typeof option.url === 'string' && /^\/pages\/[a-z0-9-]+$/.test(option.url)
              ? [{ label: option.label, url: option.url }]
              : [])
        : []
    };
  }

  function setup(root, employeeId) {
    const header = root.querySelector('.aa-workspace__header');
    const actions = header?.querySelector('.aa-workspace__header-actions');
    if (!header || !actions || root.querySelector('[data-aa-leo-expanded]')) return;

    const key = 'aa-hq-leo-chat-v1:' + employeeId;
    let turns = [];
    try {
      const stored = JSON.parse(window.sessionStorage?.getItem(key) || 'null');
      if (stored && typeof stored.at === 'number' &&
          Date.now() - stored.at < SESSION_LIFETIME && Array.isArray(stored.turns)) {
        turns = stored.turns.slice(-MAX_TURNS).map(safeTurn).filter(Boolean);
      }
    } catch (_) {
      turns = [];
    }

    function save() {
      turns = turns.slice(-MAX_TURNS);
      try {
        window.sessionStorage?.setItem(key, JSON.stringify({ at: Date.now(), turns }));
      } catch (_) {
        // Disallowed session storage is fine; the chat remains active on this page.
      }
    }

    // Headquarters renders this control with the page HTML, before auth resolves.
    // Reuse it so Leo never pops into place after navigating between pages.
    let launcher = actions.querySelector('[data-aa-leo-header-trigger]');
    if (!launcher) {
      launcher = make('button', 'aa-leo-launcher');
      launcher.type = 'button';
      launcher.setAttribute('data-aa-leo-header-trigger', '');
      launcher.setAttribute('aria-controls', 'aa-leo-expanded');
      launcher.setAttribute('aria-expanded', 'false');
      launcher.setAttribute('aria-label', 'Ask Leo, open Headquarters assistant');
      const portrait = make('img', 'aa-leo-launcher__portrait');
      portrait.src = LEO_IMAGE;
      portrait.alt = '';
      portrait.width = 54;
      portrait.height = 58;
      portrait.loading = 'eager';
      portrait.decoding = 'sync';
      portrait.setAttribute('aria-hidden', 'true');
      const wordmark = make('span', 'aa-leo-launcher__wordmark', 'Ask Leo');
      launcher.append(portrait, wordmark);
      actions.insertBefore(launcher, actions.firstChild);
    }
    launcher.disabled = false;

    const panel = make('section', 'aa-leo-expanded');
    panel.id = 'aa-leo-expanded';
    panel.setAttribute('data-aa-leo-expanded', '');
    panel.setAttribute('aria-label', 'Ask Leo');
    panel.hidden = true;

    const panelHead = make('div', 'aa-leo-expanded__head');
    const heading = make('strong', '', 'Ask Leo');
    const headActions = make('div', 'aa-leo-expanded__head-actions');
    const reset = make('button', 'aa-leo-expanded__clear', 'New chat');
    reset.type = 'button';
    reset.setAttribute('aria-label', 'Start a new Leo conversation');
    const close = make('button', 'aa-leo-expanded__close', 'Close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close Ask Leo');
    headActions.append(reset, close);
    panelHead.append(heading, headActions);

    const form = make('form', 'aa-leo-expanded__form');
    form.setAttribute('role', 'search');
    const input = make('input', 'aa-leo-expanded__input');
    input.type = 'search';
    input.name = 'leo_question';
    input.autocomplete = 'off';
    input.maxLength = 500;
    input.placeholder = 'Ask Leo anything...';
    input.setAttribute('aria-label', 'Your question for Leo');
    const send = make('button', 'aa-leo-expanded__send', 'Ask');
    send.type = 'submit';
    form.append(input, send);

    const response = make('div', 'aa-leo-expanded__response');
    response.hidden = true;
    response.setAttribute('role', 'log');
    response.setAttribute('aria-label', 'Conversation with Leo');
    response.setAttribute('aria-live', 'polite');
    response.setAttribute('aria-relevant', 'additions text');
    const log = make('div', 'aa-leo-expanded__content');
    const illustration = make('img', 'aa-leo-expanded__art');
    illustration.src = TRIO_IMAGE;
    illustration.alt = '';
    illustration.setAttribute('aria-hidden', 'true');
    illustration.loading = 'lazy';
    response.append(log, illustration);

    panel.append(panelHead, form, response);
    header.insertAdjacentElement('afterend', panel);

    function open() {
      panel.hidden = false;
      launcher.setAttribute('aria-expanded', 'true');
      scrollToBottom();
      input.focus();
    }
    function dismiss() {
      panel.hidden = true;
      launcher.setAttribute('aria-expanded', 'false');
      launcher.focus();
    }
    function scrollToBottom() {
      // Scroll message history only. The three-dog artwork belongs to the stationary outer panel.
      if (typeof log.scrollTop === 'number') log.scrollTop = log.scrollHeight;
    }

    launcher.addEventListener('click', () => panel.hidden ? open() : dismiss());
    close.addEventListener('click', dismiss);
    panel.addEventListener('keydown', event => {
      if (event.key === 'Escape') dismiss();
    });

    const routeLabels = {
      '/pages/artisans-avenue-clients': 'Open Clients',
      '/pages/artisans-avenue-my-work': 'Open My Work',
      '/pages/artisans-avenue-operations': 'Open Operations',
      '/pages/artisans-avenue-settings': 'Open Settings',
      '/pages/artisans-avenue-headquarters': 'Open Dashboard',
      '/pages/workspace-catalogs': 'Open Catalogs & Co-op'
    };

    function addTurn(turn) {
      response.hidden = false;
      const wrapper = make('article', 'aa-leo-expanded__turn aa-leo-expanded__turn--' + turn.role);
      wrapper.appendChild(make('strong', 'aa-leo-expanded__turn-label', turn.role === 'user' ? 'You:' : 'Leo:'));
      const body = make('div', 'aa-leo-expanded__turn-body');
      wrapper.appendChild(body);
      if (turn.role === 'user') {
        body.appendChild(make('p', '', turn.message));
      } else {
        if (turn.kind !== 'conversation' && turn.title && turn.title !== 'Leo') {
          body.appendChild(make('h3', '', turn.title));
        }
        if (turn.message) body.appendChild(make('p', 'aa-leo-expanded__conversation', turn.message));
        if (turn.steps?.length) {
          const list = make('ol');
          turn.steps.forEach(step => list.appendChild(make('li', '', step)));
          body.appendChild(list);
        }
        // Notes are supporting context, not part of the primary answer.
        // Keep the visible response focused on steps and the navigation action.
        if (turn.sources?.length) {
          const sources = make('div', 'aa-leo-expanded__sources');
          sources.appendChild(make('strong', '', 'Sources'));
          turn.sources.forEach(source => {
            const link = make('a', 'aa-leo-expanded__source-link', source.title);
            link.href = source.url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            sources.appendChild(link);
          });
          body.appendChild(sources);
        }
        // Different entry points to the same feature can be offered together.
        const destinations = turn.navigationOptions?.length
          ? turn.navigationOptions
          : turn.url ? [{ url: turn.url, label: routeLabels[turn.url] || 'Open this section' }] : [];
        if (destinations.length) {
          const buttons = make('div', 'aa-leo-expanded__navigation-options');
          destinations.forEach(destination => {
            const button = make('button', 'aa-leo-expanded__navigate', destination.label);
            button.type = 'button';
            button.addEventListener('click', () => window.location.assign(destination.url));
            buttons.appendChild(button);
          });
          body.appendChild(buttons);
        }
      }
      log.appendChild(wrapper);
      scrollToBottom();
      return { wrapper, body };
    }

    function renderSaved() {
      log.replaceChildren();
      response.hidden = turns.length === 0;
      turns.forEach(turn => addTurn(turn));
    }

    reset.addEventListener('click', () => {
      if (send.disabled) return;
      turns = [];
      save();
      renderSaved();
      input.value = '';
      input.focus();
    });

    function contextHistory() {
      // Only the most recent consecutive casual conversation is model context.
      // Business tasks, IDs and private client details never enter this history.
      const history = [];
      for (let i = turns.length - 1; i >= 1 && history.length < HISTORY_WINDOW; i -= 2) {
        const reply = turns[i], question = turns[i - 1];
        if (reply?.role !== 'assistant' || reply.kind !== 'conversation' ||
            question?.role !== 'user') break;
        history.unshift({ role: 'assistant', content: reply.message.slice(0, 500) });
        history.unshift({ role: 'user', content: question.message.slice(0, 500) });
      }
      return history;
    }

    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (send.disabled) return;
      const question = input.value.trim();
      if (!question) {
        input.focus();
        return;
      }

      // Visible feedback is instantaneous even if the network/model needs time.
      const history = contextHistory();
      const userTurn = safeTurn({ role: 'user', message: question });
      addTurn(userTurn);
      const waiting = addTurn({ role: 'assistant', kind: 'conversation', message: 'Typing…' });
      waiting.wrapper.setAttribute('aria-busy', 'true');
      send.disabled = true;
      input.disabled = true;
      input.value = '';

      try {
        const db = window.AASupabaseClient?.getClient?.();
        const config = window.AA_SUPABASE_CONFIG;
        if (!db || !config?.url || !config?.anonKey) {
          throw new Error('Headquarters connection unavailable.');
        }
        const { data, error } = await db.auth.getSession();
        if (error || !data?.session?.access_token) {
          throw new Error('Please sign in to Headquarters again.');
        }

        const request = await fetch(config.url + '/functions/v1/aa-leo-service', {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + data.session.access_token,
            apikey: config.anonKey,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            message: question,
            history,
            // Sent only for server-side client-name resolution. Leo's general AI
            // conversation never receives these business chat turns.
            contextQuestions: turns.filter(turn => turn.role === 'user')
              .slice(-6).map(turn => turn.message.slice(0, 500)),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago'
          })
        });
        const payload = await request.json().catch(() => ({}));
        if (!request.ok || payload.success !== true) {
          throw new Error(payload.error || 'Leo is unavailable right now.');
        }

        const guide = payload.data || {};
        const assistantTurn = safeTurn({
          role: 'assistant', kind: guide.kind, title: guide.title,
          message: guide.message, steps: guide.steps, notes: guide.notes, sources: guide.sources, url: guide.url, navigationOptions: guide.navigationOptions
        });
        if (!assistantTurn) throw new Error('Leo returned an invalid answer.');
        // Replace only the pending turn; earlier turns remain visible.
        waiting.wrapper.remove();
        addTurn(assistantTurn);
        turns.push(userTurn, assistantTurn);
        save();
      } catch (error) {
        waiting.wrapper.remove();
        addTurn({
          role: 'assistant', kind: 'conversation',
          message: error?.message || 'Leo is temporarily unavailable. Nothing was changed.'
        });
        input.value = question;
      } finally {
        send.disabled = false;
        input.disabled = false;
        if (!panel.hidden) input.focus();
      }
    });

    renderSaved();
  }

  document.addEventListener('DOMContentLoaded', async () => {
    try {
      const auth = await window.AAWorkspaceContext?.getWorkspaceAuthContext?.();
      if (!auth?.user || auth.locked || auth.isInternal !== true) return;
      document.querySelectorAll('[data-aa-headquarters]').forEach(root => setup(root, auth.user.id));
    } catch (error) {
      console.warn('Ask Leo access could not be confirmed.', error);
    }
  });
})();
